import { getApp, getApps, initializeApp } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import type { Auth, User } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const hasFirebaseConfig = Boolean(config.apiKey && config.authDomain && config.projectId && config.appId);
export let app: FirebaseApp | null = null;
export let auth: Auth | null = null;
export let db: Firestore | null = null;
export const authPersistenceReady: Promise<void> = Promise.resolve();

if (hasFirebaseConfig) {
  try {
    app = getApps().length > 0 ? getApp() : initializeApp(config);
    auth = getAuth(app);
    db = getFirestore(app);
  } catch (error) {
    console.error('Firebase initialization failed.', error);
  }
}

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: 'select_account' });

export async function prepareFirebaseAuth(): Promise<void> {
  if (!auth) throw new Error('Firebase Web App configuration is missing.');
  await setPersistence(auth, browserLocalPersistence);
}

export async function signInWithGoogle(): Promise<void> {
  if (!auth) throw new Error('Firebase Web App configuration is missing. Restart npm start after setting REACT_APP_FIREBASE_* values.');
  await prepareFirebaseAuth();
  try {
    await signInWithPopup(auth, provider);
  } catch (error: any) {
    const fallbackCodes = new Set(['auth/popup-blocked', 'auth/popup-closed-by-user', 'auth/cancelled-popup-request']);
    if (!fallbackCodes.has(error?.code)) throw error;
    await signInWithRedirect(auth, provider);
  }
}

export async function completeRedirectSignIn(): Promise<User | null> {
  if (!auth) return null;
  await prepareFirebaseAuth();
  const result = await getRedirectResult(auth);
  return result?.user ?? auth.currentUser;
}

export function subscribeToAuth(callback: (user: User | null) => void): () => void {
  return auth ? onAuthStateChanged(auth, callback) : () => {};
}

export function signOut(): Promise<void> {
  return auth ? firebaseSignOut(auth) : Promise.resolve();
}
